import { captureException } from '@sentry/browser';
import axios from 'axios';

/**
 * Single source of truth for reporting errors to console and Sentry.
 *
 * - Logs all errors to console
 * - Skips cancellations (axios cancel, AbortError)
 * - Prevents double-reporting (interceptor + catch block on same error)
 * - Enriches Axios errors with API context (fingerprint, tags, response data)
 * - Sends all other errors to Sentry as-is
 *
 * Called from:
 * - The Axios response interceptor (apis/axios.js) as a safety net for all API errors
 * - Individual catch blocks via reportError(e) for explicit error handling
 *
 * Use this instead of console.error(e) or captureException(e) in catch blocks.
 *
 * @param {Error} error - The error to report
 * @param {object} [context] - Optional Sentry context (tags, extra, fingerprint, etc.)
 */
export function reportError(error, context = {}) {
  console.error(error);

  // Skip cancellations entirely
  if (axios.isCancel(error) || error?.name === 'AbortError') return;

  // Prevent double-reporting when both the interceptor and a catch block
  // call reportError on the same error object
  if (error._sentryReported) return;
  error._sentryReported = true;

  // Enrich Axios errors with API context for better grouping and debugging
  if (axios.isAxiosError(error)) {
    // Skip 401s — these are auth redirects, not real errors
    if (error.response?.status === 401) return;

    const url = error.config?.url || 'unknown';
    // Normalize URL: strip MongoDB IDs and query params for consistent grouping
    const normalizedUrl = url
      .replace(/[a-f0-9]{24}/g, ':id')
      .replace(/\?.*$/, '');

    captureException(error, {
      fingerprint: [
        'axios-error',
        (error.config?.method || 'unknown').toLowerCase(),
        normalizedUrl,
        String(error.response?.status || 'unknown'),
      ],
      tags: {
        api_status: error.response?.status,
        api_url: error.config?.url,
        api_method: error.config?.method,
        ...(context.tags || {}),
      },
      extra: {
        response_data: error.response?.data,
        ...(context.extra || {}),
      },
    });
    return;
  }

  // Non-Axios errors: send to Sentry with any provided context
  captureException(error, context);
}
